/**
 * Perfil: como a pessoa aparece na mesa, e como ela vem indo.
 *
 * As duas coisas na mesma tela de propósito. Separadas, o perfil viraria
 * uma tela de configuração que ninguém abre — e o histórico é justamente
 * o motivo de alguém querer voltar aqui.
 *
 * Sobre o saldo do jogo: aparece com sinal, e vermelho quando é negativo.
 * A tentação de mostrar só "ganhos" existe e é exatamente o que não se
 * deve fazer num produto onde se perde dinheiro de verdade. Quem está
 * perdendo tem o direito de ver que está perdendo, no mesmo tamanho de
 * letra em que veria que está ganhando.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  AVATARES,
  Avatar,
  type AvatarId,
  CORES_AVATAR,
  type CorAvatar,
  NOME_DA_COR,
  nomeDoAvatar,
} from '../components/Avatar.tsx';
import { EsqueletoPerfil } from '../components/Esqueleto.tsx';
import { Trajetoria } from '../components/Trajetoria.tsx';
import {
  BarraDeMesa,
  CamadasDeMesa,
  SeloDeMesa,
  corDoJogo,
  molduraDeMesa,
} from '../components/MesaCard.tsx';
import {
  type HistoricoJogador,
  type PartidaDoHistorico,
  type PerfilPublico,
  aproveitamento,
  carregarPerfil,
  salvarPerfil,
} from '../lib/perfil.ts';
import { money } from '../lib/format.ts';
import { avisar } from '../lib/avisos.ts';

/**
 * Prévia: a mesma mesa que aparece no salão, com você sentado nela.
 *
 * Era um cartão branco com o avatar no meio, o que mostrava o personagem
 * mas não o contexto — e o contexto é a pergunta, porque é sobre a mesa
 * que o adversário decide se senta.
 *
 * Tema neutro de propósito: aqui não há jogo escolhido, e pintar de
 * xadrez afirmaria um que ninguém escolheu.
 *
 * Não é botão. Não há o que clicar numa prévia, e moldura que cresce sob
 * o cursor prometeria ação.
 */
export function PreviaDaMesa({
  peca,
  cor,
  nome,
}: {
  peca: string | null;
  cor: string | null;
  nome: string;
}) {
  return (
    <section
      className={`${molduraDeMesa({ interativo: false })} min-h-[12rem]`}
      style={{ ['--tema' as string]: corDoJogo('NEUTRO') }}
    >
      <CamadasDeMesa gameType="NEUTRO" />

      <div className="relative flex h-full flex-col justify-between gap-4 p-5 text-white min-h-[12rem]">
        <SeloDeMesa>Sua mesa</SeloDeMesa>

        <div className="flex items-center gap-3 min-w-0">
          <Avatar
            avatar={peca}
            cor={cor}
            tamanho={84}
            nome={nome.trim() || 'você'}
            className="outline-2 outline-white/60 shrink-0"
          />
          <div className="min-w-0">
            <p className="font-bold truncate" style={{ fontSize: 'var(--text-lead)' }}>
              {nome.trim() || 'Jogador'}
            </p>
            <p className="text-sm" style={{ color: 'rgb(255 255 255 / 0.78)' }}>
              É assim que você aparece para o adversário.
            </p>
          </div>
        </div>

        <BarraDeMesa comSeta={false}>Esperando alguém sentar</BarraDeMesa>
      </div>
    </section>
  );
}

function Numero({
  rotulo,
  valor,
  tom,
}: {
  rotulo: string;
  valor: string;
  tom?: 'bom' | 'ruim';
}) {
  return (
    <div className="card p-4 grid gap-1">
      <span className="rotulo">{rotulo}</span>
      <span
        className="font-bold tabular-nums"
        style={{
          fontSize: 'var(--text-title)',
          color:
            tom === 'bom' ? 'var(--primary)' : tom === 'ruim' ? 'var(--danger)' : undefined,
        }}
      >
        {valor}
      </span>
    </div>
  );
}

function Historico({
  h,
  trajetoria,
}: {
  h: HistoricoJogador;
  trajetoria: readonly PartidaDoHistorico[];
}) {
  const taxa = aproveitamento(h);

  if (h.partidas === 0) {
    return (
      <section className="card p-5 grid gap-2">
        <h2 className="rotulo">Seu histórico</h2>
        <p className="text-muted-foreground">
          Você ainda não terminou nenhuma partida. Depois da primeira, aparece aqui quanto
          ganhou, quanto perdeu e como vem indo.
        </p>
      </section>
    );
  }

  return (
    <section className="grid gap-3">
      <h2 className="rotulo">Seu histórico</h2>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Numero rotulo="Partidas" valor={String(h.partidas)} />
        <Numero rotulo="Vitórias" valor={String(h.vitorias)} tom="bom" />
        <Numero rotulo="Derrotas" valor={String(h.derrotas)} />
        <Numero rotulo="Empates" valor={String(h.empates)} />
      </div>

      <div className="card p-5 grid gap-3">
        {/* A curva vem antes dos números: é o que responde "estou
            melhorando ou piorando", que é a pergunta de verdade. */}
        {trajetoria.length >= 2 ? (
          <div className="grid gap-1">
            <Trajetoria pontos={trajetoria.map((t) => ({ acumuladoCents: t.acumuladoCents }))} />
            <p className="text-xs text-muted-foreground">
              Suas últimas {trajetoria.length} partidas. A linha tracejada é o zero.
            </p>
          </div>
        ) : null}

        <div className="flex items-baseline justify-between gap-4">
          <span className="rotulo">Saldo do jogo</span>
          <span
            className="money font-bold tabular-nums"
            style={{
              fontSize: 'var(--text-title)',
              color: h.saldoDoJogoCents < 0 ? 'var(--danger)' : 'var(--primary)',
            }}
          >
            {h.saldoDoJogoCents > 0 ? '+' : ''}
            {money(h.saldoDoJogoCents)}
          </span>
        </div>

        <dl className="grid grid-cols-2 gap-3 border-t pt-3 text-sm">
          <div>
            <dt className="rotulo">Você apostou</dt>
            <dd className="money">{money(h.apostadoCents)}</dd>
          </div>
          <div>
            <dt className="rotulo">Você recebeu</dt>
            <dd className="money">{money(h.recebidoCents)}</dd>
          </div>
        </dl>

        {taxa !== null ? (
          <p className="text-xs text-muted-foreground border-t pt-3">
            Você venceu {taxa}% das partidas que tiveram vencedor. Empates ficam de fora da
            conta: neles ninguém ganhou nada.
          </p>
        ) : null}
      </div>
    </section>
  );
}

export function Perfil({ token, onVoltar }: { token: string; onVoltar: () => void }) {
  const [perfil, setPerfil] = useState<PerfilPublico | null>(null);
  const [historico, setHistorico] = useState<HistoricoJogador | null>(null);
  const [trajetoria, setTrajetoria] = useState<readonly PartidaDoHistorico[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  // Rascunho separado do que está gravado: assim o botão sabe se há algo a
  // salvar, e sair da tela sem salvar não deixa a impressão de ter salvo.
  const [nome, setNome] = useState('');
  const [peca, setPeca] = useState<AvatarId>(AVATARES[0] as AvatarId);
  const [cor, setCor] = useState<CorAvatar>('areia');

  const recarregar = useCallback(async (): Promise<void> => {
    const resultado = await carregarPerfil(token);
    setCarregando(false);

    if (resultado.estado !== 'ok') {
      setErro(resultado.mensagem);
      return;
    }
    setErro(null);
    setPerfil(resultado.dado.perfil);
    setHistorico(resultado.dado.historico);
    setTrajetoria(resultado.dado.trajetoria ?? []);
    setNome(resultado.dado.perfil.displayName === 'Jogador' ? '' : resultado.dado.perfil.displayName);
    setPeca(resultado.dado.perfil.avatar as AvatarId);
    setCor(resultado.dado.perfil.avatarColor as CorAvatar);
  }, [token]);

  useEffect(() => {
    void recarregar();
  }, [recarregar]);

  const mudou =
    perfil !== null &&
    (nome.trim() !== (perfil.displayName === 'Jogador' ? '' : perfil.displayName) ||
      peca !== perfil.avatar ||
      cor !== perfil.avatarColor);

  const nomeValido = nome.trim().length >= 2 && nome.trim().length <= 24;

  async function salvar(): Promise<void> {
    setSalvando(true);
    setErro(null);
    const resultado = await salvarPerfil(token, nome.trim(), peca, cor);
    setSalvando(false);

    if (resultado.estado !== 'ok') {
      setErro(resultado.mensagem);
      return;
    }
    setPerfil(resultado.dado);
    avisar.ok('Perfil salvo', 'É assim que você aparece na mesa.');
  }

  if (carregando) return <EsqueletoPerfil />;

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-8 grid gap-6 content-start grow">
      <div className="flex items-baseline justify-between gap-4">
        <h1 className="font-bold" style={{ fontSize: 'var(--text-title)' }}>
          Seu perfil
        </h1>
        <button type="button" className="btn btn-ghost !px-3 !py-1.5" onClick={onVoltar}>
          Voltar ao salão
        </button>
      </div>

      <PreviaDaMesa peca={peca} cor={cor} nome={nome} />

      <section className="card p-5 grid gap-5">
        <label className="grid gap-1.5">
          <span className="rotulo">Nome na mesa</span>
          <input
            type="text"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            maxLength={24}
            placeholder="Como quer ser chamado"
            className="w-full rounded-lg border px-4 py-2.5 outline-none transition"
            style={{ background: 'var(--card)' }}
          />
          <span className="text-xs text-muted-foreground">
            De 2 a 24 caracteres. Seu nome real e seu CPF nunca aparecem para o adversário.
          </span>
        </label>

        <fieldset className="grid gap-2">
          <legend className="rotulo mb-1">Personagem</legend>
          <ul className="flex flex-wrap gap-2">
            {AVATARES.map((a) => (
              <li key={a}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={peca === a}
                  aria-label={nomeDoAvatar(a)}
                  title={nomeDoAvatar(a)}
                  onClick={() => setPeca(a)}
                  className="rounded-full p-1 transition"
                  style={{
                    outline: peca === a ? '2px solid var(--brass)' : '2px solid transparent',
                    outlineOffset: 2,
                  }}
                >
                  {/* `ansioso`: são 48 de uma vez, e o carregamento
                      preguiçoso encheria a grade de círculos vazios. */}
                  <Avatar avatar={a} cor={cor} tamanho={44} ansioso />
                </button>
              </li>
            ))}
          </ul>
        </fieldset>

        <fieldset className="grid gap-2">
          <legend className="rotulo mb-1">Cor</legend>
          <ul className="flex flex-wrap gap-2">
            {CORES_AVATAR.map((c) => (
              <li key={c}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={cor === c}
                  aria-label={NOME_DA_COR[c]}
                  title={NOME_DA_COR[c]}
                  onClick={() => setCor(c)}
                  className="rounded-full p-1 transition"
                  style={{
                    outline: cor === c ? '2px solid var(--brass)' : '2px solid transparent',
                    outlineOffset: 2,
                  }}
                >
                  <Avatar avatar={peca} cor={c} tamanho={36} />
                </button>
              </li>
            ))}
          </ul>
        </fieldset>

        {/* O erro fica na tela, junto do campo que o causou. Só o
            sucesso vira aviso flutuante: erro que some sozinho obriga a
            pessoa a repetir a ação para reler o que deu errado. */}
        {erro ? (
          <p className="text-sm" role="alert" style={{ color: 'var(--danger)' }}>
            {erro}
          </p>
        ) : null}

        <button
          type="button"
          className="btn btn-primary !py-3"
          disabled={salvando || !mudou || !nomeValido}
          onClick={() => void salvar()}
        >
          {salvando ? 'Salvando…' : mudou ? 'Salvar perfil' : 'Tudo salvo'}
        </button>
      </section>

      {historico ? <Historico h={historico} trajetoria={trajetoria} /> : null}
    </main>
  );
}
